import React, { useState } from 'react';
import { PlusIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import type { NewDeliverableInput } from '../../contexts/PortalContext';
import { DELIVERABLE_STATUSES, DELIVERABLE_TYPES, VIDEO_FORMATS } from '../../types/portal';
import { deliverableStage, isVersioned, stageHeading } from '../../utils/deliverables';
import { BUTTON_GHOST, BUTTON_QUIET, BUTTON_SOLID, FIELD, OPTION, SELECT } from './adminStyles';
import { TranscriptUpload } from './TranscriptUpload';
const EMPTY: NewDeliverableInput = {
  name: '',
  type: 'Video',
  quantity: '1',
  status: 'Not Started',
  version: '',
  driveUrl: '',
  audioUrl: '',
  internalNote: '',
  visibleToClient: false,
  videoFormat: '16:9 Landscape',
  transcript: ''
};
export function AddDeliverable() {
  const {
    addDeliverable,
    notify
  } = usePortal();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState<NewDeliverableInput>(EMPTY);
  function edit(patch: Partial<NewDeliverableInput>) {
    setInput(current => ({
      ...current,
      ...patch
    }));
  }
  function submit(event: React.FormEvent) {
    event.preventDefault();
    const name = input.name.trim();
    if (!name) return;
    /** Versions only exist once the status reaches Review or Final Delivery. */
    addDeliverable({
      ...input,
      name,
      version: isVersioned(input.status) ? input.version : ''
    });
    notify(`${name} added`);
    setInput(EMPTY);
    setOpen(false);
  }
  if (!open) {
    return <button type="button" onClick={() => setOpen(true)} className={`${BUTTON_GHOST} mt-8 inline-flex items-center gap-3`} data-id="element-136" data-mp="12r4x0p1ho05e">
        <PlusIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-137" data-mp="1hf811svz11rk" />
        Add Deliverable
      </button>;
  }
  return <form onSubmit={submit} className="mt-8 border border-paper/25 px-6 py-8 sm:px-8" data-id="element-138" data-mp="1cj0wa3fb9wb7">
      <h3 className="font-display text-2xl leading-none" data-id="element-139" data-mp="1qgpl1mci1nlj">New Deliverable</h3>

      <label className="mt-8 block" data-id="element-140" data-mp="1lio2d2izjqal">
        <span className="label block text-paper/40" data-id="element-141" data-mp="1lfi9rdb5e6a4">Name</span>
        <input value={input.name} onChange={event => edit({
        name: event.target.value
      })} placeholder="e.g. Speaker Interview 01" autoFocus className={FIELD} data-id="element-142" data-mp="j5i0o1nlkf6p" />
      </label>

      <div className="mt-8 grid grid-cols-2 gap-8 sm:grid-cols-3" data-id="element-143" data-mp="s2s9lgcxzs8g">
        <label className="block" data-id="element-144" data-mp="wwwi441uw8bn">
          <span className="label block text-paper/40" data-id="element-145" data-mp="11uevc6mwp9pg">Type</span>
          <select value={input.type} onChange={event => edit({
          type: event.target.value as NewDeliverableInput['type']
        })} className={SELECT} data-id="element-146" data-mp="11twnsdusn928">
            {DELIVERABLE_TYPES.map(option => <option key={option} value={option} className={OPTION} data-id="element-147" data-mp="1ftxqzzyj6skv">
                {option}
              </option>)}
          </select>
        </label>
        <label className="block" data-id="element-148" data-mp="3dls89lhambae">
          <span className="label block text-paper/40" data-id="element-149" data-mp="3hlatkid10odr">Quantity</span>
          <input value={input.quantity} onChange={event => edit({
          quantity: event.target.value
        })} placeholder="e.g. 8" className={FIELD} data-id="element-150" data-mp="3hnn9kpz84oo6" />
        </label>
        {isVersioned(input.status) ? <label className="block" data-id="element-151" data-mp="30d9xavebgvkn">
            <span className="label block text-paper/40" data-id="element-152" data-mp="30ci3nq4a57x0">Version</span>
            <input value={input.version} onChange={event => edit({
          version: event.target.value
        })} placeholder="e.g. V1" className={FIELD} data-id="element-153" data-mp="2wbog4xur1v4n" />
          </label> : <div className="block" data-id="element-154" data-mp="3nzzxrsb0oi35">
            <span className="label block text-paper/40" data-id="element-155" data-mp="3jzpp27vubdkl">Version</span>
            <p className="mt-3 border-b border-paper/25 pb-2 font-sans text-sm tracking-wide2 text-paper/30" data-id="element-156" data-mp="3k224oee4lbuh">
              Added once In Review
            </p>
          </div>}
      </div>

      <label className="mt-8 block" data-id="element-157" data-mp="28iproxumy8jp">
        <span className="label block text-paper/40" data-id="element-158" data-mp="23lpsn6w7e623">Status</span>
        <select value={input.status} onChange={event => edit({
        status: event.target.value as NewDeliverableInput['status']
      })} className={SELECT} data-id="element-159" data-mp="3rinm2jbrzaqg">
          {DELIVERABLE_STATUSES.map(option => <option key={option} value={option} className={OPTION} data-id="element-160" data-mp="3ij0ibqvkpsu8">
              {option}
            </option>)}
        </select>
        {/* Stage follows the status, so there is nothing else to set. */}
        <span className="label-sm mt-3 block text-paper/30" data-id="element-161" data-mp="e4aig0r46hfg">
          Stage · {stageHeading(deliverableStage(input.status))} · set from status
        </span>
      </label>

      {/* Only video needs framing — it decides the client player's shape. */}
      {input.type === 'Video' && <label className="mt-8 block" data-id="element-162" data-mp="a0unr9ylur5e">
          <span className="label block text-paper/40" data-id="element-163" data-mp="a40e0siyzz2p">Video Format</span>
          <select value={input.videoFormat ?? '16:9 Landscape'} onChange={event => edit({
        videoFormat: event.target.value as NewDeliverableInput['videoFormat']
      })} className={SELECT} data-id="element-164" data-mp="2prqobsc76hwv">
            {VIDEO_FORMATS.map(option => <option key={option} value={option} className={OPTION} data-id="element-165" data-mp="2i9j2h31w8y6f">
                {option}
              </option>)}
          </select>
        </label>}

      {input.type === 'Transcript' ? <TranscriptUpload value={input.transcript ?? ''} onChange={text => edit({
      transcript: text
    })} data-id="element-166" data-mp="2r8eamkoi0icd" /> : <label className="mt-8 block" data-id="element-167" data-mp="1yml2rwocidiq">
          <span className="label block text-paper/40" data-id="element-168" data-mp="1ynmm0ofim6bj">Google Drive Link</span>
          <input value={input.driveUrl} onChange={event => edit({
        driveUrl: event.target.value
      })} placeholder="Paste Drive file or folder link" className={FIELD} data-id="element-169" data-mp="2dbhi8esqestd" />
        </label>}

      <label className="mt-8 block" data-id="element-170" data-mp="1fvbprdfd96me">
        <span className="label block text-paper/40" data-id="element-171" data-mp="1fuagnehwuu7b">Notes</span>
        <input value={input.internalNote} onChange={event => edit({
        internalNote: event.target.value
      })} placeholder="Optional internal note" className={FIELD} data-id="element-172" data-mp="3fxteyzlwcqjp" />
      </label>

      <label className="mt-8 flex items-center justify-between gap-6 border-b border-paper/25 pb-3" data-id="element-173" data-mp="3pn4lcehefh3w">
        <span className="label text-paper/40" data-id="element-174" data-mp="3tnwuz8u6no0p">Visible to Client</span>
        <button type="button" onClick={() => edit({
        visibleToClient: !input.visibleToClient
      })} aria-pressed={input.visibleToClient} className={['label border px-3 py-2 transition-colors duration-150 ease-out', input.visibleToClient ? 'border-paper bg-paper text-ink' : 'border-paper/30 text-paper/40 hover:border-paper/60'].join(' ')} data-id="element-175" data-mp="2wyyz1nap5k1y">
          {input.visibleToClient ? 'On' : 'Off'}
        </button>
      </label>

      <div className="mt-10 flex flex-wrap items-center gap-6" data-id="element-176" data-mp="q77yygs87wpa">
        <button type="submit" className={BUTTON_SOLID} data-id="element-177" data-mp="10c6eyhvhoc6a">
          Add Deliverable
        </button>
        <button type="button" onClick={() => {
        setInput(EMPTY);
        setOpen(false);
      }} className={BUTTON_QUIET} data-id="element-178" data-mp="vd3fdcya2y5p">
          Cancel
        </button>
      </div>
    </form>;
}